import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  MapPin,
  Warehouse,
  Package,
  ChevronRight,
  ChevronDown,
  Edit,
  Eye,
  RefreshCw,
  Phone,
  Mail,
  User,
  Globe,
  Server,
  Wrench,
  Factory,
  Home,
  Store,
  FlaskConical,
  Plus
} from 'lucide-react';
import { branchService } from '../../../services/branchService';
import { locationService } from '../../../services/locationService';
import { warehouseService } from '../../../services/warehouseService';
import { assetService } from '../../../services/assetService';

const BranchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [branchData, allLocations, allWarehouses, allAssets] = await Promise.all([
        branchService.getBranchById(id),
        locationService.getLocationsByBranch(id).catch(() => []),
        warehouseService.getWarehousesByBranch(id).catch(() => []),
        assetService.getAssets().catch(() => []),
      ]);

      setBranch(branchData);
      setLocations(allLocations || []);
      setWarehouses(allWarehouses || []);
      setAssets(allAssets || []);

      // Expand branch by default
      setExpandedNodes({ [`branch-${id}`]: true });
    } catch (err) {
      console.error('Failed to load branch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const getTypeIcon = (type) => {
    const icons = {
      HEADQUARTERS: Building,
      WAREHOUSE: Warehouse,
      FACTORY: Factory,
      OFFICE: Home,
      SERVICE_CENTER: Wrench,
      RETAIL: Store,
      DATA_CENTER: Server,
      LABORATORY: FlaskConical,
      OTHER: Building
    };
    return icons[type] || Building;
  };

  const getStatusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      UNDER_CONSTRUCTION: 'bg-yellow-100 text-yellow-800',
      CLOSED: 'bg-red-100 text-red-800',
      OPERATIONAL: 'bg-green-100 text-green-800',
      UNDER_MAINTENANCE: 'bg-yellow-100 text-yellow-800',
      OUT_OF_SERVICE: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  // Get assets for a specific location
  const getLocationAssets = (locationId) => {
    return assets.filter(a => a.locationId === locationId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Branch not found</p>
        <Link to="/portal/cmms/branches" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Branches
        </Link>
      </div>
    );
  }

  const TypeIcon = getTypeIcon(branch.branchType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/portal/cmms/branches')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <TypeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{branch.name}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(branch.status)}`}>
                  {branch.status?.replace('_', ' ')}
                </span>
              </div>
              <p className="text-gray-500">{branch.code} - {branch.city}, {branch.country}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to={`/portal/cmms/branches?edit=${id}`} className="btn btn-outline">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tree View */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Organization Tree</h2>
              <button
                onClick={() => setExpandedNodes({ [`branch-${id}`]: true })}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                Expand All
              </button>
            </div>
            <div className="p-4">
              {/* Branch Root */}
              <div className="tree-view">
                <TreeNode
                  icon={TypeIcon}
                  iconBg="bg-blue-100"
                  iconColor="text-blue-600"
                  label={branch.name}
                  sublabel={`${branch.code} - ${branch.branchType}`}
                  expanded={expandedNodes[`branch-${id}`]}
                  onToggle={() => toggleNode(`branch-${id}`)}
                  hasChildren={locations.length > 0 || warehouses.length > 0}
                >
                  {/* Locations Section */}
                  {locations.length > 0 && (
                    <TreeNode
                      icon={MapPin}
                      iconBg="bg-purple-100"
                      iconColor="text-purple-600"
                      label="Locations"
                      sublabel={`${locations.length} location(s)`}
                      expanded={expandedNodes['locations']}
                      onToggle={() => toggleNode('locations')}
                      hasChildren={true}
                    >
                      {locations.map(location => {
                        const locationAssets = getLocationAssets(location.id);
                        return (
                          <TreeNode
                            key={location.id}
                            icon={MapPin}
                            iconBg="bg-purple-50"
                            iconColor="text-purple-500"
                            label={location.name}
                            sublabel={location.code}
                            status={location.isActive ? 'ACTIVE' : 'INACTIVE'}
                            expanded={expandedNodes[`location-${location.id}`]}
                            onToggle={() => toggleNode(`location-${location.id}`)}
                            hasChildren={locationAssets.length > 0}
                            actions={
                              <Link
                                to={`/portal/cmms/locations?view=${location.id}`}
                                className="text-primary-600 hover:text-primary-800"
                                onClick={e => e.stopPropagation()}
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                            }
                          >
                            {locationAssets.length > 0 && (
                              <TreeNode
                                icon={Package}
                                iconBg="bg-orange-100"
                                iconColor="text-orange-600"
                                label="Assets"
                                sublabel={`${locationAssets.length} asset(s)`}
                                expanded={expandedNodes[`location-${location.id}-assets`]}
                                onToggle={() => toggleNode(`location-${location.id}-assets`)}
                                hasChildren={true}
                              >
                                {locationAssets.map(asset => (
                                  <TreeNode
                                    key={asset.id}
                                    icon={Package}
                                    iconBg="bg-orange-50"
                                    iconColor="text-orange-500"
                                    label={asset.name}
                                    sublabel={asset.assetTag || asset.category}
                                    status={asset.status}
                                    hasChildren={false}
                                    actions={
                                      <Link
                                        to={`/portal/cmms/assets/${asset.id}`}
                                        className="text-primary-600 hover:text-primary-800"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Link>
                                    }
                                  />
                                ))}
                              </TreeNode>
                            )}
                          </TreeNode>
                        );
                      })}
                    </TreeNode>
                  )}

                  {/* Warehouses Section */}
                  {warehouses.length > 0 && (
                    <TreeNode
                      icon={Warehouse}
                      iconBg="bg-green-100"
                      iconColor="text-green-600"
                      label="Warehouses"
                      sublabel={`${warehouses.length} warehouse(s)`}
                      expanded={expandedNodes['warehouses']}
                      onToggle={() => toggleNode('warehouses')}
                      hasChildren={true}
                    >
                      {warehouses.map(warehouse => (
                        <TreeNode
                          key={warehouse.id}
                          icon={Warehouse}
                          iconBg="bg-green-50"
                          iconColor="text-green-500"
                          label={warehouse.name}
                          sublabel={warehouse.code}
                          status={warehouse.status}
                          hasChildren={false}
                          actions={
                            <Link
                              to={`/portal/cmms/warehouses?view=${warehouse.id}`}
                              className="text-primary-600 hover:text-primary-800"
                              onClick={e => e.stopPropagation()}
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                          }
                        />
                      ))}
                    </TreeNode>
                  )}

                  {locations.length === 0 && warehouses.length === 0 && (
                    <div className="ml-6 py-4 text-gray-500 text-sm">
                      No locations or warehouses found
                    </div>
                  )}
                </TreeNode>
              </div>
            </div>
          </div>
        </div>

        {/* Branch Info Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
            <div className="space-y-3">
              {branch.managerName && (
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{branch.managerName}</span>
                </div>
              )}
              {branch.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${branch.email}`} className="text-primary-600 hover:underline">
                    {branch.email}
                  </a>
                </div>
              )}
              {branch.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{branch.phone}</span>
                </div>
              )}
              {branch.timezone && (
                <div className="flex items-center gap-3 text-sm">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <span>{branch.timezone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Address</h2>
            <div className="text-sm text-gray-600 space-y-1">
              {branch.address && <p>{branch.address}</p>}
              <p>{branch.city}, {branch.state} {branch.postalCode}</p>
              <p>{branch.country}</p>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Statistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{locations.length}</div>
                <div className="text-xs text-gray-600">Locations</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{warehouses.length}</div>
                <div className="text-xs text-gray-600">Warehouses</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg col-span-2">
                <div className="text-2xl font-bold text-orange-600">
                  {assets.filter(a => locations.some(l => l.id === a.locationId)).length}
                </div>
                <div className="text-xs text-gray-600">Total Assets</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Link
                to={`/portal/cmms/locations?branchId=${id}`}
                className="w-full btn btn-outline justify-start"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Link>
              <Link
                to={`/portal/cmms/warehouses?branchId=${id}`}
                className="w-full btn btn-outline justify-start"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Warehouse
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tree Node Component
const TreeNode = ({
  icon: Icon,
  iconBg,
  iconColor,
  label,
  sublabel,
  status,
  expanded,
  onToggle,
  hasChildren,
  children,
  actions
}) => {
  const getStatusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-green-100 text-green-700',
      INACTIVE: 'bg-gray-100 text-gray-600',
      OPERATIONAL: 'bg-green-100 text-green-700',
      UNDER_MAINTENANCE: 'bg-yellow-100 text-yellow-700',
      OUT_OF_SERVICE: 'bg-red-100 text-red-700',
    };
    return styles[status] || '';
  };

  return (
    <div className="tree-node">
      <div
        className={`flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-gray-50 ${hasChildren ? 'cursor-pointer' : ''}`}
        onClick={hasChildren ? onToggle : undefined}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )
        ) : (
          <div className="w-4" />
        )}
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{label}</span>
            {status && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(status)}`}>
                {status.replace('_', ' ')}
              </span>
            )}
          </div>
          {sublabel && <span className="text-xs text-gray-500">{sublabel}</span>}
        </div>
        {actions && (
          <div className="flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
      {hasChildren && expanded && (
        <div className="ml-6 border-l border-gray-200 pl-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default BranchDetail;
